// src/components/Dashboard/widgets/CrewOnSiteWidget.tsx
// Widget 4 — Crew On-Site Today
// Click crew tile → Mobile & Crew Management at that crew member.
// RECALL ALL button → MobileComms mass-recall modal.
import { Users, ChevronRight, Radio, AlertCircle } from 'lucide-react'
import { MOCK_JOBS } from '../../../utils/mockData'
import { MOCK_ALLOCATIONS, WEEK_DAYS } from '../../../utils/mockDataRoster'
import { WidgetProps } from '../types'
import { useDeepLinkNavigation } from '../../../hooks/useDeepLinkNavigation'

export function CrewOnSiteWidget({ onNavigate, scopedJobIds }: WidgetProps) {
  const { navigate } = useDeepLinkNavigation('dashboard', onNavigate)

  // Use current week allocations (WEEK_DAYS is the mock "this week" array)
  const weekAllocs = MOCK_ALLOCATIONS.filter(a => WEEK_DAYS.includes(a.date))
  const scoped     = scopedJobIds
    ? weekAllocs.filter(a => scopedJobIds.includes(a.jobId))
    : weekAllocs

  const uniqueEmployees     = [...new Set(scoped.map(a => a.employeeId))]
  const onSiteCount         = uniqueEmployees.length
  const pendingTimesheets   = scoped.filter(a => !a.timesheetId).length

  // Per-job crew breakdown
  const activeJobs = MOCK_JOBS.filter(j => j.status === 'ACTIVE')
  const byJob = activeJobs
    .map(job => {
      const jobAllocs = scoped.filter(a => a.jobId === job.id)
      const empIds    = [...new Set(jobAllocs.map(a => a.employeeId))]
      return { job, count: empIds.length }
    })
    .filter(j => j.count > 0)
    .slice(0, 4)

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden flex flex-col">
      {/* Header */}
      <div
        className="px-5 py-3.5 border-b flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => navigate('mobile')}
      >
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-teal-600" />
          <span className="font-semibold text-gray-900 text-sm">Crew on-site today</span>
          <span className="text-xs bg-teal-100 text-teal-700 px-1.5 py-0.5 rounded-full font-medium">
            {onSiteCount}
          </span>
        </div>
        <ChevronRight className="w-3.5 h-3.5 text-gray-300" />
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 divide-x border-b bg-gray-50/60">
        <div className="px-4 py-2.5 text-center">
          <div className="text-xl font-bold text-teal-600">{onSiteCount}</div>
          <div className="text-xs text-gray-400 mt-0.5">On site this week</div>
        </div>
        <div className="px-4 py-2.5 text-center">
          <div className={`text-xl font-bold ${pendingTimesheets > 0 ? 'text-amber-600' : 'text-gray-300'}`}>
            {pendingTimesheets}
          </div>
          <div className="text-xs text-gray-400 mt-0.5">
            {pendingTimesheets > 0 ? (
              <span className="flex items-center justify-center gap-0.5 text-amber-600">
                <AlertCircle className="w-3 h-3" />Timesheets pending
              </span>
            ) : 'Timesheets current'}
          </div>
        </div>
      </div>

      {/* By-job breakdown */}
      <div className="divide-y divide-gray-50 flex-1">
        {byJob.map(({ job, count }) => (
          <div
            key={job.id}
            className="flex items-center gap-3 px-5 py-3 hover:bg-gray-50 cursor-pointer transition-colors group"
            onClick={() => navigate('jobs', { openId: job.id })}
          >
            <div className="w-8 h-8 rounded-lg bg-teal-50 flex items-center justify-center flex-shrink-0">
              <Users className="w-3.5 h-3.5 text-teal-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900">{job.jobNumber}</p>
              <p className="text-xs text-gray-400 truncate">{(job.description ?? '').split('—')[0].trim()}</p>
            </div>
            <div className="text-right flex-shrink-0">
              <div className="text-sm font-bold text-teal-700">{count}</div>
              <div className="text-xs text-gray-400">crew</div>
            </div>
            <ChevronRight className="w-3.5 h-3.5 text-gray-200 group-hover:text-gray-400 transition-colors" />
          </div>
        ))}

        {onSiteCount === 0 && (
          <div className="px-5 py-8 text-center">
            <Users className="w-6 h-6 text-gray-200 mx-auto mb-2" />
            <p className="text-sm text-gray-400">No crew rostered this week</p>
          </div>
        )}
      </div>

      {/* Footer actions */}
      <div className="px-5 py-2.5 border-t flex items-center justify-between text-xs">
        <button
          onClick={() => navigate('mobile')}
          className="text-blue-600 hover:underline flex items-center gap-1 font-medium"
        >
          <Radio className="w-3 h-3" />RECALL ALL
        </button>
        <button onClick={() => navigate('workforce')} className="text-blue-600 hover:underline">
          Full roster →
        </button>
      </div>
    </div>
  )
}
